import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Edit, Plus, Play, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { BackupService } from '@/lib/services/backupService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { BackupSchedule } from '@/types/backup'

function getFrequencyLabel(schedule: BackupSchedule): string {
  switch (schedule.frequency) {
    case 'daily':
      return `Daily at ${(schedule.time || '02:00').toString().slice(0, 5)}`
    case 'weekly': {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      return `Weekly on ${days[schedule.day_of_week ?? 1]} at ${(schedule.time || '02:00').toString().slice(0, 5)}`
    }
    case 'monthly':
      return `Monthly on day ${schedule.day_of_month ?? 1} at ${(schedule.time || '02:00').toString().slice(0, 5)}`
    case 'custom':
      return `Custom: ${schedule.cron_expression || 'N/A'}`
    default:
      return 'Unknown'
  }
}

export default function SchedulesPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const [schedules, setSchedules] = useState<BackupSchedule[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [deleteSchedule, setDeleteSchedule] = useState<BackupSchedule | null>(null)
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  useEffect(() => {
    fetchSchedules()
  }, [])

  const fetchSchedules = async () => {
    setIsLoading(true)
    try {
      const data = await BackupService.getSchedules()
      setSchedules(Array.isArray(data) ? data : [])
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteSchedule) return
    try {
      await BackupService.deleteSchedule(deleteSchedule.id)
      showSuccess('Schedule deleted successfully')
      onDeleteClose()
      setDeleteSchedule(null)
      fetchSchedules()
    } catch (error: any) {
      showError(error)
    }
  }

  const handleRun = async (id: number) => {
    try {
      await BackupService.runSchedule(id)
      showSuccess('Schedule triggered successfully')
      fetchSchedules()
    } catch (error: any) {
      showError(error)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <HStack justify="space-between">
              <Box>
                <Heading size="md">Backup Schedules</Heading>
                <Text fontSize="sm" color={colors.text} mt={1}>Manage automated backup schedules</Text>
              </Box>
              <Button
                leftIcon={<Plus size={18} />}
                colorScheme="blue"
                size="sm"
                onClick={() => navigate('/system-settings/backup-restore/schedules/create')}
              >
                Create Schedule
              </Button>
            </HStack>
          </CardHeader>
          <CardBody overflowY="auto" flex={1} minH={0}>
            {isLoading ? (
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading schedules...</Text>
              </VStack>
            ) : schedules.length === 0 ? (
              <VStack spacing={4} py={8}>
                <Text color="gray.500">No schedules found</Text>
                <Button leftIcon={<Plus size={18} />} colorScheme="blue" onClick={() => navigate('/system-settings/backup-restore/schedules/create')}>
                  Create First Schedule
                </Button>
              </VStack>
            ) : (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Type</Th>
                    <Th>Frequency</Th>
                    <Th>Status</Th>
                    <Th>Next Run</Th>
                    <Th>Last Run</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {schedules.map((schedule) => (
                    <Tr key={schedule.id}>
                      <Td fontWeight="medium">{schedule.name}</Td>
                      <Td>
                        <Badge colorScheme={schedule.type === 'database' ? 'blue' : schedule.type === 'files' ? 'green' : 'purple'}>{schedule.type}</Badge>
                      </Td>
                      <Td>{getFrequencyLabel(schedule)}</Td>
                      <Td>
                        <Badge colorScheme={schedule.active ? 'green' : 'gray'}>{schedule.active ? 'Active' : 'Inactive'}</Badge>
                      </Td>
                      <Td>{schedule.next_run_at ? formatDistanceToNow(new Date(schedule.next_run_at), { addSuffix: true }) : 'N/A'}</Td>
                      <Td>{schedule.last_run_at ? formatDistanceToNow(new Date(schedule.last_run_at), { addSuffix: true }) : 'Never'}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton aria-label="Run now" icon={<Play size={16} />} size="sm" colorScheme="blue" variant="ghost" onClick={() => handleRun(schedule.id)} />
                          <IconButton aria-label="Edit" icon={<Edit size={16} />} size="sm" colorScheme="blue" variant="ghost" onClick={() => navigate(`/system-settings/backup-restore/schedules/${schedule.id}`)} />
                          <IconButton aria-label="Delete" icon={<Trash2 size={16} />} size="sm" colorScheme="red" variant="ghost" onClick={() => { setDeleteSchedule(schedule); onDeleteOpen() }} />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Box>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Schedule</ModalHeader>
          <ModalBody>
            <Text>Are you sure you want to delete the schedule &quot;{deleteSchedule?.name}&quot;? This action cannot be undone.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
